'use client'

import { useState } from 'react'
import { useTranslations } from '@/lib/next-shim'
import { useSnackbar } from 'notistack'
import { getGeocode } from 'use-places-autocomplete'

import { Box, Button, Paper, Stack, TextField } from '@mui/material'
import AddIcon from '@mui/icons-material/AddOutlined'
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorderOutlined'

import { useGetMyInfosQuery, useAddSavedAddressMutation, useDeleteSavedAddressMutation } from '@/core/graphql/hooks'
import { SavedAddressList } from '@/shared/ui/address/SavedAddressList'
import { AddressDetailsForm, type AddressDetails } from '@/shared/ui/address/AddressDetailsForm'
import PlacesAutocomplete from '@/components/ui/comboboxes/PlacesAutocomplete'

import { ProfileAccordion } from './ProfileAccordion'

export function SavedAddressesSection() {
  const t = useTranslations('address')
  const tProfile = useTranslations('page.AccountSettings.profile.snackbar')
  const tRoot = useTranslations()
  const { enqueueSnackbar } = useSnackbar()
  const { data } = useGetMyInfosQuery()
  const addresses = data?.myInfos?.savedAddresses || []

  const [addSavedAddress] = useAddSavedAddressMutation({ refetchQueries: ['getMyInfos'] })
  const [deleteSavedAddress] = useDeleteSavedAddressMutation({ refetchQueries: ['getMyInfos'] })

  const [showAddForm, setShowAddForm] = useState(false)
  const [newAddress, setNewAddress] = useState({ label: '', street: '', city: '', postalCode: '', country: '' })
  const [newDetails, setNewDetails] = useState<AddressDetails>({})

  const handleAdd = async () => {
    if (!newAddress.label || !newAddress.street || !newAddress.city) return
    try {
      await addSavedAddress({
        variables: {
          payload: { ...newAddress, ...newDetails, isDefault: addresses.length === 0 },
        },
      })
      setNewAddress({ label: '', street: '', city: '', postalCode: '', country: '' })
      setNewDetails({})
      setShowAddForm(false)
      enqueueSnackbar(tProfile('saveSuccess'), { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(tProfile('saveError'), { variant: 'error' })
    }
  }

  const handleDelete = async (addressId: string) => {
    try {
      await deleteSavedAddress({ variables: { addressId } })
      enqueueSnackbar(tProfile('saveSuccess'), { variant: 'success' })
    } catch (e) {
      enqueueSnackbar(tProfile('saveError'), { variant: 'error' })
    }
  }

  // Google Places selection → geocode the prediction and auto-fill street/city/postalCode/country.
  // The fields stay editable so the patient can tweak the parsed result.
  const handleStreetSelect = async (prediction: google.maps.places.AutocompletePrediction) => {
    try {
      const [result] = await getGeocode({ placeId: prediction.place_id })
      let streetNumber = ''
      let route = ''
      let city = ''
      let postalCode = ''
      let country = ''

      for (const component of result.address_components) {
        if (component.types.includes('street_number')) streetNumber = component.long_name
        if (component.types.includes('route')) route = component.long_name
        if (component.types.includes('locality') || component.types.includes('postal_town')) city = component.long_name
        if (component.types.includes('postal_code')) postalCode = component.long_name
        if (component.types.includes('country')) country = component.long_name
      }

      const street = [streetNumber, route].filter(Boolean).join(' ') || prediction.structured_formatting.main_text
      setNewAddress((prev) => ({
        ...prev,
        street,
        city: city || prev.city,
        postalCode: postalCode || prev.postalCode,
        country: country || prev.country,
      }))
    } catch (e) {
      // Geocode failed — fall back to the raw main text so the street field isn't left empty.
      setNewAddress((prev) => ({ ...prev, street: prediction.structured_formatting.main_text }))
    }
  }

  return (
    <ProfileAccordion icon={<BookmarkBorderIcon sx={{ color: 'primary.dark' }} />} title={tRoot('address.savedAddresses')}>
      <Stack spacing={2}>
        <SavedAddressList addresses={addresses} onSelect={() => {}} onDelete={handleDelete} />

        {!showAddForm ? (
          <Button
            type="button"
            variant="text"
            startIcon={<AddIcon />}
            onClick={() => setShowAddForm(true)}
            sx={{ alignSelf: 'flex-start', color: 'primary.main', fontWeight: 600, '&:hover': { color: 'primary.dark', bgcolor: 'transparent' } }}
          >
            {t('newAddress')}
          </Button>
        ) : (
          <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'grey.50' }}>
            <Stack spacing={2.5}>
              <TextField
                label={t('addressLabel')}
                value={newAddress.label}
                placeholder={t('addressLabelPlaceholder')}
                onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
                fullWidth
              />
              <PlacesAutocomplete hideManualEntry label={t('searchAddress')} onSelect={handleStreetSelect} />
              <TextField label={t('street')} value={newAddress.street} onChange={(e) => setNewAddress({ ...newAddress, street: e.target.value })} fullWidth />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField label={t('city')} value={newAddress.city} onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })} fullWidth />
                <TextField
                  label={t('postalCode')}
                  value={newAddress.postalCode}
                  onChange={(e) => setNewAddress({ ...newAddress, postalCode: e.target.value })}
                  fullWidth
                />
              </Box>
              <TextField label={t('country')} value={newAddress.country} onChange={(e) => setNewAddress({ ...newAddress, country: e.target.value })} fullWidth />

              <AddressDetailsForm value={newDetails} onChange={setNewDetails} />

              <Stack direction="row" alignItems="center" spacing={2} sx={{ pt: 1 }}>
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleAdd}
                  disabled={!newAddress.label || !newAddress.street || !newAddress.city}
                  sx={{
                    bgcolor: 'primary.main',
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 1,
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  {t('saveAddress')}
                </Button>
                <Button type="button" variant="text" color="inherit" onClick={() => setShowAddForm(false)}>
                  {t('cancel')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}
      </Stack>
    </ProfileAccordion>
  )
}
